// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { FullStory, init as fullStoryInit, isInitialized } from '@fullstory/browser';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import type { UserInfoDto } from 'org-hub-api';

/*
 * Request header carrying the current FullStory session id to the backend on every API call. The API/BFF
 * lift it into their structured-log scope + trace span so a Kibana log / OTel trace links back to the
 * session replay. Kept in sync with the backend constant `FullStorySessionModule.HeaderName`.
 *
 * Follows the ServiceTitan `X-ST-*` header convention. Distinct from AuthZ's `X-ST-FullStory-Session`
 * (GUM-2208), which carries the full replay URL for the audit trail and is deliberately kept OFF logs —
 * this one carries the session id and is intentionally logged for correlation.
 */
export const FullStorySessionHeader = 'X-ST-FullStory-Session-Id';

/*
 * FullStory "org id" — the public data-source identifier FullStory embeds in the client bundle (not a
 * secret; same value across environments, with devMode distinguishing local dev). Standalone-only:
 * when org-hub runs embedded inside a host (Enterprise Hub / ST.Core) that host owns its own FullStory
 * session, so the embedded public MFE (`@servicetitan/org-hub`) must NOT initialize FullStory.
 * Only the standalone `application` shell does.
 *
 * This is ServiceTitan's shared FullStory account (the same org Enterprise Hub and the core app record
 * into). org-hub sessions are separated by hostname/URL (FullStory Page rules) AND by the app tags set
 * below. Set to '' to disable capture.
 */
const FULLSTORY_ORG_ID = '8HR7J';

// Stable app identifier used for both the page-scoped session tag and the human-readable app name.
const FULLSTORY_APP_NAME = 'org-hub';

/*
 * Page-scoped tags applied to every org-hub session at bootstrap — before (and independent of) user
 * identification, so even anonymous/login-screen sessions are captured with them. This is what makes
 * org-hub sessions trivially findable inside the shared ST FullStory account:
 *   - `isOrgHub` (bool) — the primary filter/segment for "show me org-hub sessions"
 *   - `application` (str)  — the app name, consistent with the value set on the user
 * Page scope (not user scope) is deliberate: it tags the SESSION, so it stays accurate for users who also
 * use other ST apps in the same account (their user record would otherwise carry a sticky org-hub flag).
 */
function tagOrgHubSession(): void {
    FullStory('setProperties', {
        type: 'page',
        properties: {
            isOrgHub: true,
            application: FULLSTORY_APP_NAME,
        },
    });
}

/*
 * Initialize FullStory once, at standalone bootstrap, before the app renders. No-op when no org id is
 * configured. `devMode` keeps local development out of the recorded data (FullStory won't record and warns
 * on SDK calls instead). Immediately tags the session so it is attributable to org-hub from the first
 * event; SDK calls made right after init are queued by the snippet until capture is ready.
 */
export function initFullStory(): void {
    if (!FULLSTORY_ORG_ID) {
        return;
    }

    fullStoryInit({
        orgId: FULLSTORY_ORG_ID,
        devMode: process.env.NODE_ENV === 'development',
    });

    tagOrgHubSession();
}

/*
 * Associate the current FullStory session with the signed-in user (from `/api/me`), attaching the ST user
 * identifiers so sessions are searchable by who they belong to and which org they were scoped to:
 *   - uid        — the FullStory user id, set to the ST titanGuid
 *   - titanGuid  — same value as a named var (matches the existing account convention, searchable by name)
 *   - displayName — FullStory's reserved display field (shown in the UI)
 *   - rootOrgId  — the org the session is scoped to
 * Guards on `isInitialized()` so it is a safe no-op when FullStory is disabled or not ready (e.g. tests, or
 * the not-yet-configured state) — never throws into the auth flow. Note: `/api/me` (UserInfoDto) carries no
 * email today; add `email` to the DTO and here if it is wanted in FullStory.
 */
export function identifyFullStoryUser(user: UserInfoDto): void {
    if (!isInitialized()) {
        return;
    }

    FullStory('setIdentity', {
        uid: user.titanGuid,
        properties: {
            titanGuid: user.titanGuid,
            displayName: user.name ?? undefined,
            rootOrgId: user.rootOrgId ?? undefined,
        },
    });
}

/*
 * The current FullStory session id, read synchronously. Returns undefined when FullStory is disabled or
 * capture hasn't started yet (e.g. the first requests right after page load) — callers omit the header then.
 */
export function getFullStorySessionId(): string | undefined {
    if (!isInitialized()) {
        return undefined;
    }

    return FullStory('getSession', { format: 'id' }) ?? undefined;
}

// Idempotent-install guard (dev HMR / double import must not register the interceptor twice).
let sessionHeaderInstalled = false;

/*
 * Attach the current FullStory session id to every outgoing API request (a global axios request
 * interceptor — the generated org-hub-api clients use the default axios instance). This is what lets the
 * backend correlate its logs/traces with the session replay. Standalone-only: installed at bootstrap
 * alongside init; the embedded MFE leaves correlation to its host.
 */
export function installFullStorySessionHeader(): void {
    if (sessionHeaderInstalled) {
        return;
    }
    sessionHeaderInstalled = true;

    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const sessionId = getFullStorySessionId();
        if (sessionId) {
            config.headers.set(FullStorySessionHeader, sessionId);
        }
        return config;
    });
}
